import * as turf from "@turf/turf";

export const getAOIMetrics = (feature: any) =>
{
      if (!feature?.geometry) return null;
      if (feature.geometry.type !== "Polygon") return null;

      const polygon = turf.polygon(feature.geometry.coordinates);

      const area = turf.area(polygon); // m²
      const perimeter = turf.length(turf.polygonToLine(polygon), {
            units: "kilometers",
      }); // km

      return {
            area,
            perimeter,
      };
};


export const WIDGETS = {
      INPUT: "input",
      TEXTAREA: "textarea",
      SELECT: "select",
      DATE: "date",
      NUMBER: "number",
      RANGE_NUMBER: "range_number"
} as const;


export const UNIT_RULES = [
      { match: ["off_nadir", "angle"], unit: "°" },
      { match: ["cloud_cover", "percent"], unit: "%" },
      { match: ["distance"], unit: "m" },
];


export const detectUnit = (key: string) =>
{
      const lowerKey = key.toLowerCase();

      for (const rule of UNIT_RULES)
      {
            if (rule.match.some((k) => lowerKey.includes(k)))
            {
                  return rule.unit;
            }
      }

      return "";
};

export const formatLabelWithUnit = (key: string, t: any) =>
{
      // format label
      const baseLabel = key
            .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
            .replace(/[_:]/g, " ")
            .toLowerCase()
            .replace(/\b\w/g, (c) => c.toUpperCase());

      const unit = detectUnit(key);

      return unit ? `${t(baseLabel)} (${unit})` : t(baseLabel);
};

export const dataKeyGeneral = [
      "geometry",
      "description_order",
      "order_name",
      "priority",
      "reason",
      "order_type",
      "request_body",
      "source"
];

// GeoJSON geometry types the AOI toolbar knows how to draw.
export const AOI_GEOMETRY_TYPES = ["Point", "Polygon"];

// Provider flows declare which AOI shapes they accept on the geometry field
// (`ui.options.geometry.supportedTypes`), so the drawing tools follow the schema
// instead of a hardcoded provider code. Returns null when the flow says nothing
// — callers decide what to fall back to.
export const getSupportedAOITypes = (workflow: any): string[] | null =>
{
      const steps: any[] = workflow?.steps ?? [];

      for (const step of steps)
      {
            const types = step?.ui?.options?.geometry?.supportedTypes;
            if (!Array.isArray(types) || types.length === 0) continue;

            // Normalize to GeoJSON casing ("point" -> "Point") so comparisons
            // against feature.geometry.type hold whatever the backend sends.
            return types.map((type: any) =>
            {
                  const match = AOI_GEOMETRY_TYPES.find(
                        (known) => known.toLowerCase() === String(type).trim().toLowerCase(),
                  );
                  return match ?? String(type);
            });
      }

      return null;
};

/**
 * Which of the AOI features already drawn the incoming flow can't take, so a
 * provider switch only clears what the new provider actually rejects and leaves
 * a shape it still supports (a Point moving between two point-capable providers)
 * on the map.
 *
 * `declaredTypes` null means the flow hasn't been read yet — nothing is dropped
 * on a guess, otherwise a provider switch would delete the feature during the
 * gap before its schema arrives.
 */
export const getUnsupportedAOIFeatures = <T extends { geometry?: { type?: string } }>(
      features: T[],
      declaredTypes: string[] | null,
): T[] =>
{
      if (!declaredTypes) return [];

      return (features ?? []).filter(
            (feature) =>
                  !declaredTypes.some(
                        (type) =>
                              type.toLowerCase() ===
                              String(feature?.geometry?.type).toLowerCase(),
                  ),
      );
};

/**
 * Whether a flow-schema field may be edited on an existing order.
 *
 * The rule is "editable unless the schema says otherwise": a field that simply
 * doesn't carry the flag is editable. Both sides of the edit form read it from
 * here — the renderer, to decide whether to disable the input, and the payload
 * builder, to decide whether to send the field. They used to disagree (`!== false`
 * vs `=== true`), which left every flag-less field editable on screen and then
 * silently dropped from the request.
 */
export const isFieldEditable = (config: any): boolean =>
      (config?.canEdit ?? config?.can_edit) !== false;

/**
 * The area a provider will accept for one order, in km².
 *
 * Declared on the same `geometry` field that carries `supportedTypes`, so it
 * arrives with the flow rather than being hard-coded per provider. Either bound
 * may be absent — a provider with only a ceiling is common, one with only a
 * floor is not, but nothing in the schema forbids it.
 */
export interface AOIAreaLimits
{
      minKm2?: number;
      maxKm2?: number;
}

const readNumber = (value: any): number | undefined =>
{
      const parsed = typeof value === "string" ? Number(value) : value;
      return typeof parsed === "number" && Number.isFinite(parsed) && parsed >= 0
            ? parsed
            : undefined;
};

/**
 * Pull the area bounds out of a provider's flow. Null when the flow has not
 * arrived or declares none — distinct from "declares no limits", so callers can
 * avoid warning about a limit they have not actually been told about yet.
 */
export const getAOIAreaLimits = (workflow: any): AOIAreaLimits | null =>
{
      const steps: any[] = workflow?.steps ?? [];

      for (const step of steps)
      {
            const geometry = step?.ui?.options?.geometry;
            if (!geometry) continue;

            const minKm2 = readNumber(geometry.min_area_km2);
            const maxKm2 = readNumber(geometry.max_area_km2);

            if (minKm2 === undefined && maxKm2 === undefined) continue;

            return { minKm2, maxKm2 };
      }

      return null;
};

export type AOIAreaStatus = "ok" | "too-small" | "too-large";

export interface AOIAreaCheck
{
      areaKm2: number;
      status: AOIAreaStatus;
}

/**
 * Measure a polygon against the provider's bounds.
 *
 * Only polygons are measured: a Point AOI has no area of its own — providers
 * that accept points expand them to a fixed scene size — so checking one
 * against an area limit would reject it for being zero.
 */
export const checkAOIArea = (
      feature: any,
      limits: AOIAreaLimits | null,
): AOIAreaCheck | null =>
{
      if (!limits) return null;
      if (feature?.geometry?.type !== "Polygon") return null;

      const metrics = getAOIMetrics(feature);
      if (!metrics) return null;

      const areaKm2 = metrics.area / 1_000_000;

      if (limits.maxKm2 !== undefined && areaKm2 > limits.maxKm2)
      {
            return { areaKm2, status: "too-large" };
      }
      if (limits.minKm2 !== undefined && areaKm2 < limits.minKm2)
      {
            return { areaKm2, status: "too-small" };
      }

      return { areaKm2, status: "ok" };
};
